import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useState } from "react";

export default function Nav() {
    const { scrollY } = useScroll();
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        return scrollY.on("change", (y) => setScrolled(y > 80));
    }, [scrollY]);

    const opacity = useTransform(scrollY, [0, 200], [0, 1]);

    return (
        <>
            {/* Announcement banner button — bold blood-red at the very top */}
            <a
                href="https://www.givesendgo.com/justicefordanielle?utm_source=share"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="top-givesendgo-bar"
                className="fixed top-0 left-0 right-0 z-[60] block bg-red-800 text-neutral-50 hover:bg-red-900 transition-colors duration-500 shadow-lg"
            >
                <div className="max-w-[92rem] mx-auto flex items-center justify-center gap-3 sm:gap-4 flex-wrap px-4 sm:px-6 py-3 sm:py-4 font-sans text-sm sm:text-base uppercase tracking-[0.15em] font-extrabold">
                    {/* GiveSendGo brand badge */}
                    <span
                        aria-hidden="true"
                        className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-sm shadow-sm"
                    >
                        {/* Starburst mark — GiveSendGo brand teal */}
                        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" className="shrink-0">
                            <g fill="#18b8a8">
                                {/* 8-point radial burst */}
                                <rect x="10.75" y="1" width="2.5" height="7" rx="1.25" />
                                <rect x="10.75" y="16" width="2.5" height="7" rx="1.25" />
                                <rect x="1" y="10.75" width="7" height="2.5" rx="1.25" />
                                <rect x="16" y="10.75" width="7" height="2.5" rx="1.25" />
                                <rect x="10.75" y="1" width="2.5" height="7" rx="1.25" transform="rotate(45 12 12)" />
                                <rect x="10.75" y="16" width="2.5" height="7" rx="1.25" transform="rotate(45 12 12)" />
                                <rect x="1" y="10.75" width="7" height="2.5" rx="1.25" transform="rotate(45 12 12)" />
                                <rect x="16" y="10.75" width="7" height="2.5" rx="1.25" transform="rotate(45 12 12)" />
                            </g>
                        </svg>
                        <span className="text-[#2c2b34] text-xs sm:text-sm tracking-[0.02em] font-black normal-case">
                            GiveSendGo
                        </span>
                    </span>
                    <span aria-hidden="true" className="hidden sm:inline text-amber-200/70">·</span>
                    <span className="text-center">
                        Please help donate to Danielle&rsquo;s legal fund — against the largest hospital healthcare system in NY
                    </span>
                    <span aria-hidden="true" className="text-amber-200">→</span>
                </div>
            </a>

            <motion.nav
                style={{ opacity }}
                data-testid="site-nav"
                className={`fixed top-[46px] sm:top-[54px] left-0 right-0 z-50 px-6 sm:px-12 lg:px-16 py-4 sm:py-5 bg-[color:var(--paper)]/85 backdrop-blur-md border-b border-neutral-300 transition-shadow duration-500 ${scrolled ? "shadow-[0_1px_0_0_rgba(0,0,0,0.05)]" : ""}`}
            >
                <div className="max-w-[92rem] mx-auto flex items-center justify-between">
                    <a
                        href="#top"
                        data-testid="nav-home"
                        className="font-serif text-lg sm:text-xl md:text-2xl text-neutral-950 leading-tight font-bold"
                    >
                        <span className="not-italic">Justice for Sweet Danielle</span>
                        <span className="hidden md:inline text-neutral-600 italic font-normal"> · Her Last 41 Days on This Earth</span>
                    </a>
                    <div className="hidden sm:flex items-center gap-6 lg:gap-7 font-sans text-xs uppercase tracking-[0.18em] text-neutral-900 font-semibold">
                        <a href="#mission" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-mission">Mission</a>
                        <a href="#story" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-story">Story</a>
                        <a href="#timeline" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-timeline">Timeline</a>
                        <a href="#thelaw" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-thelaw">The Law</a>
                        <a href="#song" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-song">Song</a>
                        <a href="#candle" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-candle">Candle</a>
                        <a href="#letters" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-letters">Letters</a>
                        <a href="#follow" className="ink-link hover:text-amber-600 transition-colors" data-testid="nav-follow">Follow</a>
                        <a
                            href="https://www.givesendgo.com/justicefordanielle?utm_source=share"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="nav-donate"
                            className="group relative inline-flex items-center gap-2 bg-neutral-900 text-neutral-50 px-5 py-3 text-xs tracking-[0.2em] font-bold overflow-hidden"
                        >
                            <span className="absolute inset-0 bg-amber-500 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.77,0,0.175,1)]" />
                            <span className="relative">Donate</span>
                            <span className="relative">→</span>
                        </a>
                    </div>
                    {/* Mobile donate — always visible */}
                    <a
                        href="https://www.givesendgo.com/justicefordanielle?utm_source=share"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-donate-mobile"
                        className="sm:hidden inline-flex items-center bg-neutral-900 text-neutral-50 px-4 py-2.5 font-sans text-xs uppercase tracking-[0.2em] font-bold"
                    >
                        Donate →
                    </a>
                </div>
            </motion.nav>
        </>
    );
}
